import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';

interface ErrorContext {
  component: string;
  action: string;
  userId?: string;
  itemId?: string;
  additionalData?: Record<string, any>;
}

interface RecoveryAction {
  id: string;
  label: string;
  description: string;
  action: () => Promise<void>;
  severity: 'low' | 'medium' | 'high';
}

interface ErrorState {
  errors: Array<{
    id: string;
    message: string;
    context: ErrorContext;
    timestamp: Date;
    resolved: boolean;
    recoveryActions: RecoveryAction[];
  }>;
  isRecovering: boolean;
}

export const useErrorRecovery = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorState, setErrorState] = useState<ErrorState>({
    errors: [],
    isRecovering: false
  });

  const reportError = useCallback(async (
    error: Error | string,
    context: ErrorContext,
    recoveryActions: RecoveryAction[] = []
  ) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Log error to database
    try {
      await supabase
        .from('analytics_events')
        .insert({
          user_id: user?.id || null,
          event_type: 'error_reported',
          event_data: {
            error_id: errorId,
            message: errorMessage,
            context: JSON.parse(JSON.stringify(context)),
            stack: typeof error === 'object' ? error.stack : null,
            timestamp: new Date().toISOString(),
            recovery_actions_available: recoveryActions.length
          }
        });
    } catch (logError) {
      console.error('Failed to log error:', logError);
    }

    // Add default recovery actions based on context
    const defaultRecoveryActions = generateDefaultRecoveryActions(context);
    const allRecoveryActions = [...recoveryActions, ...defaultRecoveryActions];

    // Add to error state
    const newError = {
      id: errorId,
      message: errorMessage,
      context,
      timestamp: new Date(),
      resolved: false,
      recoveryActions: allRecoveryActions
    };

    setErrorState(prev => ({
      ...prev,
      errors: [newError, ...prev.errors.slice(0, 9)] // Keep last 10 errors
    }));

    // Show user-friendly toast notification
    const userFriendlyMessage = getUserFriendlyMessage(errorMessage, context);
    toast({
      title: "Something went wrong",
      description: userFriendlyMessage,
      variant: "destructive",
    });

    return errorId;
  }, [user?.id, toast]);

  const generateDefaultRecoveryActions = (context: ErrorContext): RecoveryAction[] => {
    const actions: RecoveryAction[] = [];

    // Refresh data action
    actions.push({
      id: 'refresh_data',
      label: 'Refresh Data',
      description: 'Reload the current data to resolve temporary issues',
      severity: 'low',
      action: async () => {
        await queryClient.invalidateQueries();
        toast({
          title: "Data Refreshed",
          description: "The page data has been refreshed successfully."
        });
      }
    });

    // Component-specific recovery actions
    switch (context.component) {
      case 'ProcessingBridge':
        actions.push({
          id: 'retry_processing',
          label: 'Retry Processing',
          description: 'Attempt to reprocess the failed item',
          severity: 'medium',
          action: async () => {
            if (context.itemId) {
              const { error } = await supabase.functions.invoke('enhanced-process-bill', {
                body: { queue_item_id: context.itemId }
              });
              
              if (error) throw new Error(error.message);
              
              toast({
                title: "Processing Retry Started",
                description: "The item has been queued for reprocessing."
              });
            }
          }
        });
        break;

      case 'BillQueue':
        actions.push({
          id: 'reset_assignment',
          label: 'Reset Assignment',
          description: 'Clear the current assignment and allow reassignment',
          severity: 'medium',
          action: async () => {
            if (context.itemId) {
              const { error } = await supabase
                .from('bill_upload_queue')
                .update({
                  assigned_to_account_id: null,
                  assignment_status: 'unassigned',
                  notes: 'Assignment reset due to error recovery'
                })
                .eq('id', context.itemId);

              if (error) throw new Error(error.message);
              
              await queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
              
              toast({
                title: "Assignment Reset",
                description: "The item assignment has been cleared."
              });
            }
          }
        });
        break;

      case 'FileUpload':
        actions.push({
          id: 'clear_upload_cache',
          label: 'Clear Upload Cache',
          description: 'Clear any cached upload data that might be causing issues',
          severity: 'low',
          action: async () => {
            localStorage.removeItem('upload_progress');
            localStorage.removeItem('upload_queue');
            
            toast({
              title: "Cache Cleared",
              description: "Upload cache has been cleared."
            });
          }
        });
        break;
    }

    // Generic fallback actions
    if (context.action === 'data_fetch') {
      actions.push({
        id: 'fallback_data',
        label: 'Use Cached Data',
        description: 'Temporarily use cached data while we resolve the issue',
        severity: 'low',
        action: async () => {
          // Enable offline mode or use cached data
          toast({
            title: "Using Cached Data",
            description: "Switched to cached data temporarily."
          });
        }
      });
    }

    return actions;
  };

  const getUserFriendlyMessage = (errorMessage: string, context: ErrorContext): string => {
    // Convert technical errors to user-friendly messages
    const errorMappings: Record<string, string> = {
      'Network error': 'Connection issue. Please check your internet connection.',
      'Timeout': 'The operation took too long. Please try again.',
      'Permission denied': 'You don\'t have permission to perform this action.',
      'Not found': 'The requested item could not be found.',
      'Validation failed': 'Some required information is missing or invalid.',
      'Processing failed': 'We encountered an issue while processing your request.',
      'Authentication failed': 'Please log in again to continue.',
      'Rate limit exceeded': 'Too many requests. Please wait a moment and try again.',
      'Storage full': 'Storage capacity reached. Please clear some space.',
      'Invalid file format': 'The file format is not supported. Please use a different file.',
      'File too large': 'The file is too large. Please use a smaller file.',
      'Database error': 'We\'re experiencing technical difficulties. Please try again later.'
    };

    // Check for exact matches first
    for (const [key, message] of Object.entries(errorMappings)) {
      if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
        return message;
      }
    }

    // Context-specific messages
    switch (context.component) {
      case 'ProcessingBridge':
        return 'We had trouble processing your bill. Please try uploading it again.';
      case 'BillQueue':
        return 'There was an issue with the bill queue. Please refresh and try again.';
      case 'FileUpload':
        return 'File upload failed. Please check the file and try again.';
      default:
        return 'Something unexpected happened. Please try again or contact support if the issue persists.';
    }
  };

  const executeRecovery = useCallback(async (errorId: string, actionId: string) => {
    setErrorState(prev => ({ ...prev, isRecovering: true }));

    try {
      const error = errorState.errors.find(e => e.id === errorId);
      const action = error?.recoveryActions.find(a => a.id === actionId);

      if (!action) {
        throw new Error('Recovery action not found');
      }

      await action.action();

      // Mark error as resolved
      setErrorState(prev => ({
        ...prev,
        errors: prev.errors.map(e => 
          e.id === errorId ? { ...e, resolved: true } : e
        ),
        isRecovering: false
      }));

      // Log successful recovery
      await supabase
        .from('analytics_events')
        .insert({
          user_id: user?.id || null,
          event_type: 'error_recovery_success',
          event_data: {
            error_id: errorId,
            action_id: actionId,
            timestamp: new Date().toISOString()
          }
        });

      toast({
        title: "Recovery Successful",
        description: `${action.label} completed successfully.`
      });

    } catch (recoveryError: any) {
      console.error('Recovery action failed:', recoveryError);
      
      // Log failed recovery
      await supabase
        .from('analytics_events')
        .insert({
          user_id: user?.id || null,
          event_type: 'error_recovery_failed',
          event_data: {
            error_id: errorId,
            action_id: actionId,
            error_message: recoveryError.message,
            timestamp: new Date().toISOString()
          }
        });

      toast({
        title: "Recovery Failed",
        description: `${recoveryError.message || 'Recovery action failed'}`,
        variant: "destructive"
      });
    } finally {
      setErrorState(prev => ({ ...prev, isRecovering: false }));
    }
  }, [errorState.errors, user?.id, toast]);

  const clearError = useCallback((errorId: string) => {
    setErrorState(prev => ({
      ...prev,
      errors: prev.errors.filter(e => e.id !== errorId)
    }));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrorState(prev => ({
      ...prev,
      errors: []
    }));
  }, []);

  return {
    reportError,
    executeRecovery,
    clearError,
    clearAllErrors,
    errors: errorState.errors,
    isRecovering: errorState.isRecovering,
    hasUnresolvedErrors: errorState.errors.some(e => !e.resolved)
  };
};